import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Books, Users, Megaphone, ChartBar, ClipboardText,
  Plus, Trash, GraduationCap, CheckCircle, Circle, FileText, Sparkle, Clock,
} from "@phosphor-icons/react";

const CONTENT_KINDS = {
  announcement: { label: "Annonce", icon: Megaphone, color: "text-accent" },
  lesson: { label: "Leçon", icon: FileText, color: "text-primary" },
  resource: { label: "Ressource", icon: Books, color: "text-secondary" },
  homework_note: { label: "Devoir", icon: ClipboardText, color: "text-accent" },
};

export default function CourseWorkspace() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openContent, setOpenContent] = useState(false);
  const [openGrade, setOpenGrade] = useState(false);
  const [contentForm, setContentForm] = useState({ title: "", body: "", kind: "announcement", published: true });
  const [gradeForm, setGradeForm] = useState({ student_id: "", period: "", assessment_type: "devoir", score: "", max_score: 20, comment: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/courses/${courseId}/workspace`);
      setWs(r.data);
      if (r.data.periods?.length && !gradeForm.period) {
        const cur = r.data.periods.find(p => p.is_current) || r.data.periods[0];
        setGradeForm(g => ({ ...g, period: cur.name }));
      }
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/app");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId]);

  if (loading) return <div className="p-10 text-sm text-muted-foreground">Chargement du cours…</div>;
  if (!ws) return null;

  const { course, subject, class_group, grade_level, academic_year, teachers, students, periods, content, grades_by_period, attendance_last_30_days } = ws;

  const saveContent = async () => {
    try {
      await api.post("/course-contents", { ...contentForm, course_id: courseId });
      toast.success("Publication ajoutée");
      setOpenContent(false);
      setContentForm({ title: "", body: "", kind: "announcement", published: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delContent = async (id) => {
    if (!window.confirm("Supprimer cette publication ?")) return;
    try { await api.delete(`/course-contents/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveGrade = async () => {
    try {
      await api.post("/grades", {
        ...gradeForm,
        subject_id: subject.id,
        class_id: class_group.id,
        score: Number(gradeForm.score),
        max_score: Number(gradeForm.max_score),
      });
      toast.success("Note enregistrée");
      setOpenGrade(false);
      setGradeForm({ ...gradeForm, student_id: "", score: "", comment: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const attTotal = Object.values(attendance_last_30_days).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => nav("/app/enseignant")} className="mb-3 -ml-2" data-testid="btn-back-teacher">
          <ArrowLeft size={16} className="mr-1" /> Retour au tableau de bord
        </Button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-secondary font-semibold">
              {academic_year?.name || "—"} · {class_group?.name || "—"}
              {grade_level ? ` · ${grade_level.name}` : ""}
            </div>
            <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Books size={30} weight="duotone" className="text-primary" />
              {subject?.name}
              {subject?.code && <span className="text-sm text-muted-foreground font-mono font-normal">({subject.code})</span>}
            </h1>
            <div className="mt-2 flex items-center gap-2 flex-wrap text-sm text-muted-foreground">
              {course.coefficient && <Badge variant="outline">Coef. {course.coefficient}</Badge>}
              {course.weekly_hours > 0 && <Badge variant="outline">{course.weekly_hours}h/sem.</Badge>}
              {course.room && <Badge variant="outline">Salle {course.room}</Badge>}
              {course.group_name && <Badge variant="outline">Groupe {course.group_name}</Badge>}
              {course.is_optional && <Badge variant="outline" className="border-accent text-accent">Optionnel</Badge>}
              {course.is_elective && <Badge variant="outline" className="border-accent text-accent">Électif</Badge>}
            </div>
          </div>
          <div className="flex -space-x-2">
            {teachers.map(t => (
              <div key={t.id} className="w-9 h-9 rounded-full bg-primary text-primary-foreground border-2 border-background flex items-center justify-center text-xs font-semibold"
                   title={t.name} data-testid={`teacher-avatar-${t.id}`}>
                {(t.name || "?").split(" ").map(x => x[0]).join("").slice(0, 2).toUpperCase()}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="tabs-course">
          <TabsTrigger value="overview" data-testid="tab-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content">Contenu ({content.length})</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">Devoirs</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students">Élèves ({students.length})</TabsTrigger>
          <TabsTrigger value="grades" data-testid="tab-grades">Notes</TabsTrigger>
        </TabsList>

        {/* ---------------- OVERVIEW ---------------- */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-5">
              <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Effectif</div>
              <div className="mt-2 font-heading text-4xl font-bold">{students.length}</div>
              <div className="text-xs text-muted-foreground mt-1">élèves inscrits · {class_group?.capacity || "?"} places</div>
              <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${Math.min(100, (students.length / (class_group?.capacity || 1)) * 100)}%` }} />
              </div>
            </Card>

            <Card className="p-5">
              <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Notes saisies</div>
              <div className="mt-3 space-y-2.5">
                {periods.map(p => {
                  const g = grades_by_period[p.name] || { count: 0, expected: 0, complete: false };
                  return (
                    <div key={p.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {g.complete ? <CheckCircle size={16} weight="fill" className="text-secondary" /> : <Circle size={16} className="text-muted-foreground" />}
                        <span>{p.name}</span>
                        {p.is_current && <Badge variant="outline" className="text-[9px] py-0 border-secondary text-secondary">En cours</Badge>}
                      </div>
                      <span className="tabular-nums text-muted-foreground">{g.count}/{g.expected || students.length}</span>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="p-5">
              <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Présences · 30 j.</div>
              {attTotal === 0 ? (
                <div className="mt-3 text-sm text-muted-foreground">Aucune présence enregistrée.</div>
              ) : (
                <div className="mt-3 space-y-1.5 text-sm">
                  <AttRow label="Présent" v={attendance_last_30_days.present} total={attTotal} color="bg-secondary" />
                  <AttRow label="Absent" v={attendance_last_30_days.absent} total={attTotal} color="bg-accent" />
                  <AttRow label="Retard" v={attendance_last_30_days.late} total={attTotal} color="bg-primary/60" />
                  <AttRow label="Excusé" v={attendance_last_30_days.excused} total={attTotal} color="bg-muted-foreground" />
                </div>
              )}
              <Link to="/app/presences">
                <Button variant="outline" size="sm" className="w-full mt-4" data-testid="btn-goto-attendance">
                  Faire l'appel <ClipboardText size={14} className="ml-1" />
                </Button>
              </Link>
            </Card>
          </div>
        </TabsContent>

        {/* ---------------- CONTENT ---------------- */}
        <TabsContent value="content" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-muted-foreground">Annonces, leçons et ressources publiées pour <b>{class_group?.name}</b></div>
            <Dialog open={openContent} onOpenChange={setOpenContent}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-content">
                  <Plus size={16} className="mr-2" /> Nouvelle publication
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouvelle publication</DialogTitle>
                  <DialogDescription>Publiez une annonce, une leçon ou une ressource pour cette classe.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4">
                  <div>
                    <Label>Type</Label>
                    <Select value={contentForm.kind} onValueChange={v => setContentForm({ ...contentForm, kind: v })}>
                      <SelectTrigger data-testid="select-content-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CONTENT_KINDS).map(([v, k]) => <SelectItem key={v} value={v}>{k.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Titre</Label><Input value={contentForm.title} onChange={e => setContentForm({ ...contentForm, title: e.target.value })} data-testid="input-content-title" /></div>
                  <div><Label>Contenu</Label><Textarea rows={5} value={contentForm.body} onChange={e => setContentForm({ ...contentForm, body: e.target.value })} data-testid="input-content-body" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenContent(false)}>Annuler</Button>
                  <Button onClick={saveContent} data-testid="btn-save-content">Publier</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {content.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground text-sm">
              Aucune publication. Créez une annonce ou déposez une leçon pour communiquer avec la classe.
            </Card>
          ) : (
            <div className="space-y-3">
              {content.map(c => {
                const kind = CONTENT_KINDS[c.kind] || CONTENT_KINDS.announcement;
                const K = kind.icon;
                return (
                  <Card key={c.id} className="p-4" data-testid={`card-content-${c.id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-md bg-muted flex items-center justify-center ${kind.color}`}>
                        <K size={18} weight="duotone" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-heading font-semibold">{c.title}</div>
                          <Badge variant="outline" className="text-[10px]">{kind.label}</Badge>
                        </div>
                        {c.body && <div className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">{c.body}</div>}
                        <div className="mt-2 text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString("fr-FR")}</div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => delContent(c.id)}><Trash size={16} className="text-destructive" /></Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ---------------- ASSIGNMENTS ---------------- */}
        <TabsContent value="assignments" className="mt-6">
          <AssignmentsPanel courseId={courseId} periods={periods} students={students} />
        </TabsContent>

        {/* ---------------- STUDENTS ---------------- */}
        <TabsContent value="students" className="mt-6">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/50">
                  <th className="px-4 py-3">Matricule</th>
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3">Prénom</th>
                  <th className="px-4 py-3">Genre</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={s.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/20" : ""}`} data-testid={`row-student-${s.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{s.matricule}</td>
                    <td className="px-4 py-3 font-medium">{s.last_name}</td>
                    <td className="px-4 py-3">{s.first_name}</td>
                    <td className="px-4 py-3 text-xs">{s.gender === "F" ? "♀ Fille" : "♂ Garçon"}</td>
                  </tr>
                ))}
                {students.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucun élève inscrit dans cette classe pour l'année.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* ---------------- GRADES ---------------- */}
        <TabsContent value="grades" className="mt-6">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
            <div className="text-sm text-muted-foreground">
              Saisie des notes pour <b>{subject?.name}</b> · <b>{class_group?.name}</b>
            </div>
            <Dialog open={openGrade} onOpenChange={setOpenGrade}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-grade">
                  <Plus size={16} className="mr-2" /> Nouvelle note
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouvelle note</DialogTitle>
                  <DialogDescription>Saisir une note pour un élève, une période et un type d'évaluation.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4">
                  <div>
                    <Label>Élève</Label>
                    <Select value={gradeForm.student_id} onValueChange={v => setGradeForm({ ...gradeForm, student_id: v })}>
                      <SelectTrigger data-testid="select-grade-student"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.last_name} {s.first_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Période</Label>
                    <Select value={gradeForm.period} onValueChange={v => setGradeForm({ ...gradeForm, period: v })}>
                      <SelectTrigger data-testid="select-grade-period"><SelectValue /></SelectTrigger>
                      <SelectContent>{periods.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={gradeForm.assessment_type} onValueChange={v => setGradeForm({ ...gradeForm, assessment_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="devoir">Devoir</SelectItem>
                        <SelectItem value="composition">Composition</SelectItem>
                        <SelectItem value="examen">Examen</SelectItem>
                        <SelectItem value="interrogation">Interrogation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Note</Label><Input type="number" step="0.25" value={gradeForm.score} onChange={e => setGradeForm({ ...gradeForm, score: e.target.value })} data-testid="input-grade-score" /></div>
                    <div><Label>Sur</Label><Input type="number" value={gradeForm.max_score} onChange={e => setGradeForm({ ...gradeForm, max_score: e.target.value })} /></div>
                  </div>
                  <div><Label>Commentaire</Label><Textarea rows={2} value={gradeForm.comment} onChange={e => setGradeForm({ ...gradeForm, comment: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenGrade(false)}>Annuler</Button>
                  <Button onClick={saveGrade} data-testid="btn-save-grade">Enregistrer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {periods.map(p => {
              const g = grades_by_period[p.name] || { count: 0, expected: 0, complete: false };
              const pct = students.length ? Math.round((g.count / students.length) * 100) : 0;
              return (
                <Card key={p.id} className={`p-5 ${p.is_current ? "border-secondary ring-1 ring-secondary/40" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-heading font-semibold">{p.name}</div>
                    {p.is_current && <Badge variant="outline" className="border-secondary text-secondary">En cours</Badge>}
                  </div>
                  <div className="mt-3 font-heading text-3xl font-bold tabular-nums">{g.count}<span className="text-base text-muted-foreground font-normal">/{students.length}</span></div>
                  <div className="text-xs text-muted-foreground mt-1">notes saisies · {pct}%</div>
                  <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full ${g.complete ? "bg-secondary" : "bg-accent"}`} style={{ width: `${pct}%` }} />
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="mt-6">
            <Link to="/app/notes">
              <Button variant="outline" data-testid="btn-goto-grades-full">
                <GraduationCap size={16} className="mr-2" /> Aller à la saisie complète (avec bulletins)
              </Button>
            </Link>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AttRow({ label, v, total, color }) {
  const pct = total ? Math.round((v / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span>{label}</span>
        <span className="tabular-nums text-muted-foreground">{v} · {pct}%</span>
      </div>
      <div className="mt-0.5 h-1 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const ASSIGNMENT_KIND_LABEL = {
  homework: "Devoir", exercise: "Exercice", quiz: "Quiz", test: "Test",
  exam: "Examen", project: "Projet", presentation: "Exposé",
  participation: "Participation", practical: "TP", essay: "Rédaction",
  custom: "Autre",
};

const ACCEPTED_UPLOAD = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.txt,.csv,.zip";

function AssignmentsPanel({ courseId, periods, students }) {
  const [assignments, setAssignments] = useState([]);
  const [subs, setSubs] = useState({});     // aid → [submissions]
  const [openNew, setOpenNew] = useState(false);
  const [openGrade, setOpenGrade] = useState(null);   // { assignment, submission }
  const [form, setForm] = useState({ title: "", description: "", instructions: "", kind: "homework", category: "", weight: 1, due_date: "", max_score: 20, status: "draft", period: "", allow_late_submission: true, attachments: [] });
  const [uploading, setUploading] = useState(false);
  const [gradeForm, setGradeForm] = useState({ score: "", feedback: "" });

  const load = async () => {
    try {
      const r = await api.get(`/assignments?course_id=${courseId}`);
      setAssignments(r.data);
      const map = {};
      for (const a of r.data) {
        const s = await api.get(`/submissions?assignment_id=${a.id}`);
        map[a.id] = s.data;
      }
      setSubs(map);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId]);

  const save = async () => {
    try {
      const payload = { ...form, course_id: courseId, max_score: Number(form.max_score), weight: Number(form.weight) || 1 };
      if (!payload.period) delete payload.period;
      await api.post("/assignments", payload);
      toast.success("Devoir créé");
      setOpenNew(false);
      setForm({ title: "", description: "", instructions: "", kind: "homework", category: "", weight: 1, due_date: "", max_score: 20, status: "draft", period: "", allow_late_submission: true, attachments: [] });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const uploadFiles = async (fileList) => {
    setUploading(true);
    const files = Array.from(fileList || []);
    const added = [];
    for (const f of files) {
      if (f.size > 20 * 1024 * 1024) { toast.error(`${f.name} > 20 Mo`); continue; }
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.onerror = reject; fr.readAsDataURL(f);
        });
        const r = await api.post("/uploads/document", { data_url: dataUrl, filename: f.name, kind: "assignment" });
        added.push({ name: f.name, url: r.data.url, size: r.data.size, mime: r.data.mime, ext: r.data.ext });
      } catch (e) { toast.error(`${f.name}: ${formatApiError(e)}`); }
    }
    if (added.length) setForm(fm => ({ ...fm, attachments: [...(fm.attachments || []), ...added] }));
    setUploading(false);
  };
  const publish = async (a) => {
    try { await api.post(`/assignments/${a.id}/publish`); toast.success("Publié"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (a) => {
    if (!window.confirm("Supprimer ce devoir et ses remises ?")) return;
    try { await api.delete(`/assignments/${a.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const grade = async () => {
    if (!openGrade) return;
    try {
      await api.post(`/submissions/${openGrade.submission.id}/grade`, {
        score: Number(gradeForm.score), feedback: gradeForm.feedback,
      });
      toast.success("Note enregistrée");
      setOpenGrade(null); setGradeForm({ score: "", feedback: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="text-sm text-muted-foreground">Devoirs, projets et examens de ce cours</div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-assignment">
              <Plus size={16} className="mr-2" /> Nouveau devoir
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau devoir</DialogTitle>
              <DialogDescription>Créez un devoir. En brouillon il n'est visible que par vous ; publié, il est envoyé aux élèves.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Titre</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="input-assignment-title" /></div>
              <div><Label>Description / consigne</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="input-assignment-desc" /></div>
              <div><Label>Instructions détaillées (optionnel)</Label><Textarea rows={3} value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} placeholder="Consignes complètes, critères, format attendu…" data-testid="input-assignment-instructions" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="select-assignment-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ASSIGNMENT_KIND_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Note sur</Label><Input type="number" value={form.max_score} onChange={e => setForm({ ...form, max_score: e.target.value })} data-testid="input-assignment-max" /></div>
                <div><Label>Coefficient</Label><Input type="number" step="0.5" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} data-testid="input-assignment-weight" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>À rendre avant</Label><Input type="datetime-local" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="input-assignment-due" /></div>
                <div>
                  <Label>Période</Label>
                  <Select value={form.period || "auto"} onValueChange={v => setForm({ ...form, period: v === "auto" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Auto" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (période active)</SelectItem>
                      {periods.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.allow_late_submission} onChange={e => setForm({ ...form, allow_late_submission: e.target.checked })} />
                Autoriser les remises tardives
              </label>
              <div>
                <Label>Pièces jointes (PDF, Word, Excel, PowerPoint, images)</Label>
                <div className="border-2 border-dashed border-border rounded-md p-3 mt-1 text-center hover:border-primary/40 transition">
                  <input type="file" multiple accept={ACCEPTED_UPLOAD} className="hidden" id="assignment-file-input"
                         onChange={e => uploadFiles(e.target.files)} data-testid="input-assignment-files" />
                  <label htmlFor="assignment-file-input" className="cursor-pointer text-xs text-muted-foreground">
                    <FileText size={20} className="mx-auto mb-1 text-primary" />
                    {uploading ? "Envoi en cours…" : "Glisser-déposer ou cliquer pour téléverser (max 20 Mo par fichier)"}
                  </label>
                </div>
                {form.attachments.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="assignment-attachments-list">
                    {form.attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-muted/40 rounded px-2 py-1.5 text-xs" data-testid={`attach-${idx}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={14} className="text-primary shrink-0" />
                          <span className="truncate">{att.name}</span>
                          <Badge variant="outline" className="text-[9px] shrink-0">{att.ext?.toUpperCase()}</Badge>
                          <span className="text-muted-foreground text-[10px] shrink-0">{Math.round(att.size / 1024)} Ko</span>
                        </div>
                        <Button variant="ghost" size="icon" className="h-6 w-6"
                                onClick={() => setForm(fm => ({ ...fm, attachments: fm.attachments.filter((_, i) => i !== idx) }))}
                                data-testid={`btn-remove-attach-${idx}`}>
                          <Trash size={12} className="text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label>Statut initial</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="select-assignment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="published">Publier immédiatement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenNew(false)}>Annuler</Button>
              <Button onClick={save} data-testid="btn-save-assignment">Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {assignments.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground text-sm">
          Aucun devoir pour ce cours. Créez-en un pour donner du travail à la classe.
        </Card>
      ) : (
        <div className="space-y-3">
          {assignments.map(a => {
            const list = subs[a.id] || [];
            const gradedCount = list.filter(s => s.status === "graded").length;
            const submittedCount = list.length;
            return (
              <Card key={a.id} className="p-4" data-testid={`card-assignment-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading font-semibold">{a.title}</div>
                      <Badge variant="outline" className="text-[10px]">{ASSIGNMENT_KIND_LABEL[a.kind] || a.kind}</Badge>
                      {a.status === "draft" && <Badge variant="outline" className="text-[10px] border-muted-foreground">Brouillon</Badge>}
                      {a.status === "published" && <Badge className="text-[10px] bg-secondary text-secondary-foreground">Publié</Badge>}
                      {a.status === "closed" && <Badge className="text-[10px] bg-muted text-muted-foreground">Clôturé</Badge>}
                    </div>
                    {a.description && <div className="mt-1 text-sm text-muted-foreground line-clamp-2">{a.description}</div>}
                    {(a.attachments || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`attachments-${a.id}`}>
                        {a.attachments.map((att, i) => (
                          <a key={i} href={att.url} target="_blank" rel="noreferrer"
                             className="text-[11px] inline-flex items-center gap-1 bg-muted/50 hover:bg-muted rounded px-1.5 py-0.5 border border-border"
                             data-testid={`assignment-${a.id}-file-${i}`}>
                            <FileText size={10} className="text-primary" /> {att.name}
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <span>Note /{a.max_score}</span>
                      {a.due_date && <span className="flex items-center gap-1"><Clock size={12} /> Avant {new Date(a.due_date).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                      {a.period && <span>Période {a.period}</span>}
                      <span className="font-mono">{submittedCount}/{students.length} rendus · {gradedCount} corrigés</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {a.status === "draft" && <Button size="sm" variant="outline" onClick={() => publish(a)} data-testid={`btn-publish-${a.id}`}><Sparkle size={14} className="mr-1" />Publier</Button>}
                    <Button size="sm" variant="ghost" onClick={() => del(a)}><Trash size={14} className="text-destructive" /></Button>
                  </div>
                </div>
                {list.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Remises</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {list.map(s => {
                        const st = students.find(x => x.id === s.student_id);
                        const hasAtt = (s.attachments || []).length > 0;
                        return (
                          <div key={s.id} className="text-xs bg-muted/40 rounded px-3 py-2" data-testid={`sub-${s.id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">{st ? `${st.last_name} ${st.first_name}` : "Élève"}</div>
                                <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                  <span>{s.status === "late" ? "En retard · " : ""}{new Date(s.submitted_at).toLocaleString("fr-FR")}</span>
                                  {s.submitted_handwritten && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-accent/15 text-accent font-semibold" data-testid={`badge-handwritten-${s.id}`}>
                                      ✍️ Manuscrit
                                    </span>
                                  )}
                                  {hasAtt && <span className="text-primary font-semibold">📎 {s.attachments.length} fichier(s)</span>}
                                </div>
                              </div>
                              {s.status === "graded" ? (
                                <span className="font-mono font-semibold text-secondary shrink-0">{s.grade}/{a.max_score}</span>
                              ) : (
                                <Button size="sm" variant="outline" className="h-6 text-[10px] shrink-0" onClick={() => { setOpenGrade({ assignment: a, submission: s }); setGradeForm({ score: "", feedback: "" }); }}
                                        data-testid={`btn-grade-sub-${s.id}`}>
                                  Corriger
                                </Button>
                              )}
                            </div>
                            {hasAtt && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {s.attachments.map((f, i) => (
                                  <a key={i} href={f.url?.startsWith("http") ? f.url : `${process.env.REACT_APP_BACKEND_URL}${f.url}`}
                                     target="_blank" rel="noreferrer"
                                     className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border bg-background hover:bg-primary/5"
                                     data-testid={`sub-file-${s.id}-${i}`}>
                                    <FileText size={9} /> {f.filename}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!openGrade} onOpenChange={(v) => { if (!v) setOpenGrade(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Corriger la remise</DialogTitle>
            <DialogDescription>{openGrade?.assignment?.title} · note sur {openGrade?.assignment?.max_score}</DialogDescription>
          </DialogHeader>
          {openGrade?.submission?.submitted_handwritten && (
            <div className="flex items-center gap-2 p-3 rounded bg-accent/10 border border-accent/30 text-accent text-sm" data-testid="grade-handwritten-badge">
              <span>✍️</span>
              <b>Remis en manuscrit</b> — l'élève a rendu ce travail sur papier.
            </div>
          )}
          {openGrade?.submission?.content && (
            <div className="text-sm bg-muted/40 rounded p-3 whitespace-pre-wrap max-h-40 overflow-y-auto border-l-2 border-primary">
              {openGrade.submission.content}
            </div>
          )}
          {(openGrade?.submission?.attachments || []).length > 0 && (
            <div>
              <Label>Pièces jointes de l'élève ({openGrade.submission.attachments.length})</Label>
              <div className="space-y-1 mt-1">
                {openGrade.submission.attachments.map((f, i) => (
                  <a key={i} href={f.url?.startsWith("http") ? f.url : `${process.env.REACT_APP_BACKEND_URL}${f.url}`}
                     target="_blank" rel="noreferrer"
                     className="flex items-center gap-2 text-xs p-2 bg-muted rounded hover:bg-muted/70"
                     data-testid={`grade-file-${i}`}>
                    <FileText size={12} /> {f.filename}
                    {f.size ? <span className="text-muted-foreground">({Math.round(f.size / 1024)} Ko)</span> : null}
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Note</Label><Input type="number" step="0.25" value={gradeForm.score} onChange={e => setGradeForm({ ...gradeForm, score: e.target.value })} data-testid="input-sub-score" /></div>
            <div><Label>Sur</Label><Input disabled value={openGrade?.assignment?.max_score || 20} /></div>
          </div>
          <div><Label>Retour à l'élève</Label><Textarea rows={3} value={gradeForm.feedback} onChange={e => setGradeForm({ ...gradeForm, feedback: e.target.value })} data-testid="input-sub-feedback" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenGrade(null)}>Annuler</Button>
            <Button onClick={grade} data-testid="btn-save-sub-grade">Valider la note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
